'use client'

import { useEffect } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import { isNativePlatform } from '@/lib/utils/platform'
import { rememberRoute } from '@/lib/native/last-route'

/** Idle window before flushing the route write to native storage.
 *
 *  Pages with active filtering (admin members search, map filters, infinite-
 *  scroll lists) rewrite the searchParams on every keystroke. Without a
 *  debounce that's a Capacitor Preferences `set` per keystroke — JNI/JSI
 *  bridge call → native disk write — at >10 writes/sec sustained on rapid
 *  typing. The bridge serializes through a single queue so excess writes
 *  pile up and risk an out-of-order persisted state on rapid route changes.
 *
 *  250ms is short enough that any user pause during navigation flushes the
 *  current route, but long enough to coalesce keystroke storms. */
const FLUSH_DEBOUNCE_MS = 250

/**
 * Persists the current route to Capacitor Preferences on every nav change
 * so cold launches can restore the deep surface (`<NativeRedirect>` reads
 * it). Lives in its own client component because `useSearchParams` must
 * be wrapped in a `<Suspense>` boundary in Next 15+/16 — without that,
 * the entire subtree above is forced to client-render at runtime and a
 * "Missing Suspense boundary" warning floods Sentry on every native nav.
 *
 * Writes are debounced to coalesce keystroke-driven query-param storms
 * (filter typing) into a single bridge call after a brief idle window.
 *
 * Renders nothing.
 *
 * No-op on web. The dynamic-imported `@capacitor/preferences` plugin
 * silently throws on web; `rememberRoute` swallows that.
 */
export function RouteRememberer() {
  const pathname = usePathname()
  const searchParams = useSearchParams()

  useEffect(() => {
    if (!isNativePlatform()) return
    if (!pathname) return
    const search = searchParams?.toString() ? `?${searchParams.toString()}` : ''
    const timer = setTimeout(() => {
      void rememberRoute(pathname, search)
    }, FLUSH_DEBOUNCE_MS)
    // Cleanup on next nav change OR unmount cancels the pending write so
    // we never persist a stale intermediate state. The trailing route the
    // user actually settles on flushes once the debounce window closes.
    return () => clearTimeout(timer)
  }, [pathname, searchParams])

  return null
}
